// Tremor Callout [v0.0.1]

import { forwardRef } from 'react'

import { type VariantProps, cva } from 'class-variance-authority'

import { cn } from '../utils'

const calloutVariants = cva('flex flex-col overflow-hidden border-0.5 rounded-xl p-4 text-sm', {
  defaultVariants: {
    variant: 'default',
  },
  variants: {
    variant: {
      default: [
        // text color
        'text-blue-900 dark:text-blue-400',
        // background color
        'bg-blue-50 dark:bg-blue-950/70',
        'border-blue-500 dark:border-blue-700',
      ],
      error: [
        // text color
        'text-red-900 dark:text-red-500',
        // background color
        'bg-red-50 dark:bg-red-950/70',
        'border-red-500 dark:border-red-700',
      ],
      neutral: [
        // text color
        'text-stone-900 dark:text-stone-400',
        // background color
        'bg-stone-100 dark:bg-stone-800/70',
        'border-stone-500 dark:border-stone-700',
      ],
      purple: [
        // text color
        'text-purple-900 dark:text-purple-100',
        // background color
        'bg-purple-50 dark:bg-purple-900/70',
        'border-purple-500 dark:border-purple-700',
      ],
      success: [
        // text color
        'text-emerald-900 dark:text-emerald-200',
        // background color
        'bg-emerald-50 dark:bg-emerald-900/60',
        'border-emerald-500 dark:border-emerald-700',
      ],
      warning: [
        // text color
        'text-yellow-900 dark:text-yellow-500',
        // background color
        'bg-yellow-50 dark:bg-yellow-950/70',
        'border-yellow-500 dark:border-yellow-700',
      ],
    },
  },
})

interface ICalloutProps extends VariantProps<typeof calloutVariants> {
  children?: React.ReactNode
  className?: string
  icon?: React.ElementType | React.ReactElement
  title: React.ReactNode | string
}

const Callout = forwardRef<HTMLDivElement, ICalloutProps>(
  ({ children, className, icon: Icon, title, variant, ...props }, forwardedRef) => {
    return (
      <div className={cn(calloutVariants({ variant }), className)} ref={forwardedRef} {...props}>
        <div className={cn('flex items-start md:items-center gap-2')}>
          {Icon && typeof Icon === 'function' ? (
            <Icon aria-hidden="true" className={cn('mt-1 md:mt-0 size-4 shrink-0')} />
          ) : (
            Icon
          )}
          <div className={cn('font-semibold')}>{title}</div>
        </div>
        <div className={cn('overflow-y-auto', children ? 'mt-2' : '')}>{children}</div>
      </div>
    )
  }
)

Callout.displayName = 'Callout'

export { Callout, type ICalloutProps, calloutVariants }
